import { IntegrationsTracking } from "@/lib/tracking/integrations";
import { Alert, AlertDescription } from "@altimateai/lego";
import { Input, Label } from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import styles from "./styles.module.scss";
import { CopyButton } from "../../components/copyButton";
import { getBackendUrl, getTenant, isFreeStack } from "../../helpers/tenant";
import { useQuery } from "@tanstack/react-query";
import { GET_INTEGRATION_BY_ID } from "./constants";
import { getIntegrationByID } from "../../helpers/apis";
import { RelativeComponentLoader } from "../../components/loader";
import { DataTable } from "../../components/tables";
import classNames from "classnames";
import dayjs from "dayjs";
import { ScrollContainer } from "../../components/scrollContainer";
import { default as InfoGray } from "@/assets/icons/info_gray.svg?react";
import { getDownloadUrl } from "@/helpers/apis";
import { downloadFileFromUrl } from "@/helpers/utils";
import { useState, useMemo } from "react";
import { ROCodeEditor } from "@/components/codeEditor";
import { useAppState } from "@/modules/app/useAppContext";
import type { FC } from "react";

type IntegrationDetailsProps = {
  dbtCoreIntegrationId: string;
  dbtCoreIntegrationEnv: string;
};

export const IntegrationDetails: FC<IntegrationDetailsProps> = ({
  dbtCoreIntegrationId,
  dbtCoreIntegrationEnv,
}) => {
  const [errorMsg, setErrorMsg] = useState("");
  const tenant = getTenant();
  const backendURL = getBackendUrl();
  const { dbtWorkloadEnabled, loggedinUser, enableCloudIntegration } = useAppState();
  const auth_token = loggedinUser?.auth_token;
  const isFree = isFreeStack();
  const commonCommands = ["dbt compile", "dbt docs generate", "dbt source freshness"];

  // Fetch integration details
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [GET_INTEGRATION_BY_ID, dbtCoreIntegrationId, dbtCoreIntegrationEnv],
    refetchInterval: 10000,
    queryFn: () => getIntegrationByID(dbtCoreIntegrationId, dbtCoreIntegrationEnv),
  });

  // Compute commands using useMemo
  const command = useMemo(() => {
    if (!data) return "";

    const baseCommand = `datapilot dbt onboard --backend-url ${backendURL} --token ${auth_token} --instance-name ${tenant} --manifest-path ./target/manifest.json --catalog-path ./target/catalog.json --run-results-path ./target/run_results.json --semantic-manifest-path ./target/semantic_manifest.json --sources-path ./target/sources.json`;
    let completeCommand = "";
    if (data.integration_type === "dbt_core") {
      completeCommand = `${baseCommand} --dbt_core_integration_id ${dbtCoreIntegrationId} --dbt_core_integration_environment ${data.environment_name}`;
    } else if (data.integration_type === "dbt_cloud") {
      completeCommand = `${baseCommand} --dbt_integration_id ${dbtCoreIntegrationId} --dbt_integration_environment ${data.environment_name}`;
    }
    return commonCommands.concat(completeCommand).join("\n");
  }, [data, backendURL, auth_token, tenant, dbtCoreIntegrationId]);

  // Compute query tag using useMemo
  const queryTag = useMemo(() => {
    if (!data) return "";
    return JSON.stringify(
      {
        dbt_integration_id: parseInt(dbtCoreIntegrationId, 10),
        dbt_integration_environment: data.environment_name,
      },
      null,
      2
    );
  }, [data, dbtCoreIntegrationId]);

  // Handle loading and error states
  if (isLoading) {
    return <RelativeComponentLoader label="Loading integration details" />;
  }

  if (isError) {
    // Extract error message from API response, or use default message
    // API interceptor returns error as a string (see api_helper.ts line 67)
    const errorMessage =
      typeof error === "string"
        ? error
        : (error as { message?: string })?.message ||
          "Failed to load integration details. Please try again.";

    return (
      <div className="p-4">
        <Alert variant="destructive" className={"border-0 text-black mb-0"}>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <ScrollContainer>
      <div className={styles.command}>
        <h3 className="text-black">Details</h3>

        {data?.name && (
          <div className="mb-3">
            <Label className="form-label">Integration Name</Label>
            <Input disabled value={data.name} readOnly />
          </div>
        )}

        {data?.integration_type && (
          <div className="mb-3">
            <Label className="form-label">Type of Integration</Label>
            <Input
              disabled
              value={data.integration_type === "dbt_core" ? "dbt Core" : "dbt Cloud"}
              readOnly
            />
          </div>
        )}

        {data?.environment_name && (
          <div className="mb-3">
            <Label className="form-label">Environment</Label>
            <Input disabled value={data.environment_name} readOnly />
          </div>
        )}

        {command && (
          <>
            <div className="mb-1">
              <Label className="form-label">
                Command
                {enableCloudIntegration && data?.integration_type === "dbt_cloud"
                  ? " (Optional)"
                  : ""}
              </Label>
              {/* Separate landmark so tests can target the command editor without colliding with query tag editor */}
              <section aria-label="Command code editor">
                <ROCodeEditor lineNumbers={true} height="16em" value={command} showCopy={false} />
              </section>
            </div>

            <div className={styles.command_info}>
              <div>
                <InfoGray />
              </div>
              <div className={styles.text}>
                You need to run these commands from the dbt project root directory. If you are not
                using default manifest and catalog paths, you will need to update the paths in the
                command.
              </div>
            </div>

            <CopyButton
              value={command}
              label="Copy Command"
              copiedLabel="Command Copied"
              onCopy={() => IntegrationsTracking.onboardingCommandCopied(dbtCoreIntegrationId)}
            />
          </>
        )}

        {!isFree && dbtWorkloadEnabled && queryTag.length && (
          <section aria-label="Query tag configuration">
            <div className="mb-1">
              <Label className="form-label">Enable Query Tags</Label>
              {/* Separate landmark so tests can target the query tag editor distinctly */}
              <section aria-label="Query tag code editor">
                <ROCodeEditor lineNumbers={false} height="6em" value={queryTag} showCopy={false} />
              </section>
            </div>

            <div className={styles.command_info}>
              <div>
                <InfoGray />
              </div>
              <div className={classNames(styles.text, "al-flex al-flex-col al-gap-1")}>
                Add this to your query_tag parameter in profiles.yml or dbt Cloud connection
                settings.
                <a
                  className={"al-text-brand"}
                  rel={"noreferrer"}
                  href={
                    data?.integration_type === "dbt_cloud"
                      ? "https://docs.altimate.ai/docs/dbt-cloud"
                      : "https://docs.altimate.ai/docs/dbt-core"
                  }
                  target={"_blank"}
                >
                  View complete setup guide
                </a>
              </div>
            </div>

            <div>
              <CopyButton
                value={JSON.stringify(JSON.parse(queryTag))}
                label="Copy Query Tag"
                copiedLabel="Query Tag Copied"
              />
            </div>
          </section>
        )}

        <div className="mb-3">
          <Label className="form-label">Sync History</Label>
          {errorMsg ? (
            <Alert variant="destructive" className={"border-0 text-black mb-0"}>
              <AlertDescription>{errorMsg}</AlertDescription>
            </Alert>
          ) : null}
          <DataTable
            header={[
              {
                id: 1,
                label: "Type",
                uid: "type",
                render: item => <div className="al-truncate">{item.type}</div>,
              },
              {
                id: 2,
                label: "Time",
                uid: "time",
                render: item => (
                  <div className="al-truncate">{dayjs(item.time).format("DD/MM/YYYY hh:mm a")}</div>
                ),
              },
              {
                id: 3,
                label: "Logs",
                uid: "logs",
                render: item =>
                  item.log_file ? (
                    <Button
                      variant="link"
                      onClick={async () => {
                        try {
                          const result = await getDownloadUrl(item.log_file!);
                          if (result?.url) {
                            downloadFileFromUrl(result.url);
                            setErrorMsg("");
                          } else {
                            setErrorMsg("Unable to find log file");
                          }
                        } catch (e) {
                          console.log(e);
                          setErrorMsg(String(e));
                        }
                      }}
                      className="cursor-pointer text-primary"
                    >
                      Download
                    </Button>
                  ) : (
                    <div />
                  ),
              },
            ]}
            // Defensive: API may error or return no data; default to empty array to avoid crashes
            data={{ items: data?.sync_history ?? [] }}
            // Use combination of type, time, and log_file as unique key
            // Items without log_file use 'default_log_file' as fallback
            // This ensures uniqueness for most cases; duplicates only occur if same type+time+no log_file
            idKey={item => `${item.type}-${item.time}-${item.log_file ?? "default_log_file"}`}
            minWidth={350}
          />
        </div>
      </div>
    </ScrollContainer>
  );
};
